import React, {
  FC,
  FocusEventHandler,
  KeyboardEventHandler,
  ReactNode,
  Ref,
  useEffect,
  useState,
} from 'react'
import cn from 'classnames'
import { keys } from '@hoxtonmix/utils'

export const sizeClassNames = {
  default: '',
  large: 'form-control-lg',
  small: 'form-control-sm',
}

export type InputType = Omit<
  React.InputHTMLAttributes<HTMLInputElement | HTMLTextAreaElement>,
  'size'
> & {
  isValid?: boolean
  inputClassName?: string
  iconRight?: ReactNode
  iconLeft?: ReactNode
  textarea?: boolean
  touched?: boolean
  onIconLeftClick?: () => void
  onIconRightClick?: () => void
  shouldValidate?: boolean
  size?: keyof typeof sizeClassNames
  inputRef?: Ref<HTMLInputElement>
}

type InputElement = HTMLInputElement | HTMLTextAreaElement

const Input: FC<InputType> = ({
  children,
  className,
  disabled,
  iconLeft,
  iconRight,
  id,
  inputClassName,
  inputRef,
  isValid = true,
  name,
  onBlur,
  onFocus,
  onIconLeftClick,
  onIconRightClick,
  onKeyDown,
  placeholder = ' ',
  shouldValidate: _shouldValidate,
  size = 'default',
  textarea,
  touched,
  type,
  value,
  ...rest
}) => {
  const [shouldValidate, setShouldValidate] = useState(false)
  const [isFocused, setIsFocused] = useState(false)
  const [showPassword, setShowPassword] = useState(false)

  useEffect(() => {
    if (_shouldValidate) {
      setShouldValidate(true)
    }
  }, [_shouldValidate])

  const handleFocus: FocusEventHandler<InputElement> = (e) => {
    setIsFocused(true)
    onFocus?.(e)
  }

  const handleBlur: FocusEventHandler<InputElement> = (e) => {
    setShouldValidate(true)
    setIsFocused(false)
    onBlur?.(e)
  }

  const handleKeyDown: KeyboardEventHandler<InputElement> = (e) => {
    if (keys.isEscape(e)) {
      e.currentTarget.blur()
    }
    onKeyDown?.(e)
  }

  const containerClassName = cn(
    'position-relative input-container',
    {
      focused: isFocused,
      'input-container--with-left-icon': !!iconLeft,
      'input-container--with-right-icon': !!iconRight,
      invalid: (shouldValidate || touched) && !isValid,
    },
    className,
  )

  const combinedInputClassName = cn(
    {
      error: !(shouldValidate || touched) && !isValid,
      'form-control': true,
    },
    sizeClassNames[size],
    inputClassName,
  )

  const sharedProps = {
    className: combinedInputClassName,
    id,
    name,
    onBlur: handleBlur,
    onFocus: handleFocus,
    onKeyDown: handleKeyDown,
    placeholder,
    value,
  }

  const resolvedType = type === 'password' && showPassword ? 'text' : type

  const iconClassName = (side: 'left' | 'right', onClick?: () => void) =>
    cn(`input-icon icon-${side}`, {
      'icon-disabled': disabled,
      'pe-none': !onClick,
    })

  return (
    <div className={containerClassName}>
      {textarea ? (
        <textarea {...rest} {...sharedProps} />
      ) : (
        <>
          {!!iconLeft && (
            <span
              onClick={onIconLeftClick}
              className={iconClassName('left', onIconLeftClick)}
            >
              {iconLeft}
            </span>
          )}
          <input
            disabled={disabled}
            type={resolvedType}
            {...rest}
            {...sharedProps}
            ref={inputRef}
          />
          {iconRight ? (
            <span
              onClick={onIconRightClick}
              className={iconClassName('right', onIconRightClick)}
            >
              {iconRight}
            </span>
          ) : (
            type === 'password' && (
              <i
                onClick={() => setShowPassword((prev) => !prev)}
                className={cn('input-icon icon-right', {
                  'fas fa-eye': !showPassword,
                  'fas fa-eye-slash': showPassword,
                  'icon-disabled': disabled,
                })}
              />
            )
          )}
        </>
      )}
      {children}
    </div>
  )
}

Input.displayName = 'Input'
export default Input
